import React from "react";
import {
  Box,
  Button,
  CardContent,
  Divider,
  FormControlLabel,
  MenuItem,
  Select,
  Switch,
  Typography,
} from "@mui/material";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import SaveIcon from "@mui/icons-material/Save";
import color from "../../components/utils/Colors";
import { TabPanel } from "./Settings";

// ── Validation schema ──────────────────────────────────────────────────────────
const privacySchema = Yup.object({
  profileVisibility: Yup.string()
    .oneOf(["public", "private", "friends", "instructors"], "Invalid visibility option")
    .required("Please select a visibility option"),
  showActivity: Yup.boolean().required(),
  allowMessages: Yup.boolean().required(),
  dataCollection: Yup.boolean().required(),
});

// ── Props ──────────────────────────────────────────────────────────────────────
interface PrivacyValues {
  profileVisibility: string;
  showActivity: boolean;
  allowMessages: boolean;
  dataCollection: boolean;
}

interface PrivacyTabProps {
  currentTab: number;
  initialValues: PrivacyValues;
  onSave: (values: PrivacyValues) => void;
}

// ── Component ──────────────────────────────────────────────────────────────────
const PrivacyTab: React.FC<PrivacyTabProps> = ({
  currentTab,
  initialValues,
  onSave,
}) => {
  return (
    <TabPanel value={currentTab} index={2}>
      <CardContent sx={{ padding: "30px" }}>
        <Formik
          initialValues={initialValues}
          validationSchema={privacySchema}
          enableReinitialize
          onSubmit={(values) => onSave(values)}
        >
          {({ values, setFieldValue, isSubmitting, errors, touched }) => (
            <Form>
              {/* ── Profile Visibility ───────────────────────────────── */}
              <Typography
                variant="h6"
                sx={{
                  color: color.textColor,
                  fontWeight: "bold",
                  marginBottom: "20px",
                }}
              >
                Profile Visibility
              </Typography>

              <Box sx={{ marginBottom: "30px" }}>
                <Typography
                  variant="body2"
                  sx={{ color: "#666", marginBottom: "12px" }}
                >
                  Who can see your profile?
                </Typography>
                <Select
                  fullWidth
                  value={values.profileVisibility}
                  onChange={(e) =>
                    setFieldValue("profileVisibility", e.target.value)
                  }
                  error={
                    touched.profileVisibility &&
                    Boolean(errors.profileVisibility)
                  }
                  sx={{ maxWidth: "400px" }}
                >
                  <MenuItem value="public">Public — everyone can see</MenuItem>
                  <MenuItem value="private">Private — only you</MenuItem>
                  <MenuItem value="friends">Friends only</MenuItem>
                  <MenuItem value="instructors">Instructors only</MenuItem>
                </Select>
                {touched.profileVisibility && errors.profileVisibility && (
                  <Typography variant="body2" sx={{ color: "#d32f2f", mt: 0.5 }}>
                    {errors.profileVisibility}
                  </Typography>
                )}
              </Box>

              <Divider sx={{ marginY: "30px" }} />

              {/* ── Activity Sharing ─────────────────────────────────── */}
              <Typography
                variant="h6"
                sx={{
                  color: color.textColor,
                  fontWeight: "bold",
                  marginBottom: "20px",
                }}
              >
                Activity Sharing
              </Typography>

              <Box sx={{ marginBottom: "30px" }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={values.showActivity}
                      onChange={(e) =>
                        setFieldValue("showActivity", e.target.checked)
                      }
                    />
                  }
                  label="Show my learning activity"
                />
                <Typography
                  variant="body2"
                  sx={{ color: "#999", marginLeft: "32px" }}
                >
                  Allow others to see what courses you're taking
                </Typography>
              </Box>

              <Divider sx={{ marginY: "30px" }} />

              {/* ── Communication ────────────────────────────────────── */}
              <Typography
                variant="h6"
                sx={{
                  color: color.textColor,
                  fontWeight: "bold",
                  marginBottom: "20px",
                }}
              >
                Communication
              </Typography>

              <Box sx={{ marginBottom: "30px" }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={values.allowMessages}
                      onChange={(e) =>
                        setFieldValue("allowMessages", e.target.checked)
                      }
                    />
                  }
                  label="Allow direct messages from other users"
                />
                <Typography
                  variant="body2"
                  sx={{ color: "#999", marginLeft: "32px" }}
                >
                  Control who can send you messages
                </Typography>
              </Box>

              <Divider sx={{ marginY: "30px" }} />

              {/* ── Data & Analytics ─────────────────────────────────── */}
              <Typography
                variant="h6"
                sx={{
                  color: color.textColor,
                  fontWeight: "bold",
                  marginBottom: "20px",
                }}
              >
                Data & Analytics
              </Typography>

              <Box sx={{ marginBottom: "30px" }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={values.dataCollection}
                      onChange={(e) =>
                        setFieldValue("dataCollection", e.target.checked)
                      }
                    />
                  }
                  label="Allow G2I to collect usage analytics"
                />
                <Typography
                  variant="body2"
                  sx={{ color: "#999", marginLeft: "32px" }}
                >
                  Help us improve by sharing anonymous usage data
                </Typography>
              </Box>

              <Button
                type="submit"
                variant="contained"
                startIcon={<SaveIcon />}
                disabled={isSubmitting}
                sx={{
                  background: color.firstColor,
                  color: "white",
                  textTransform: "none",
                  fontWeight: "bold",
                  "&:hover": { background: color.forthColor },
                }}
              >
                Save Privacy Settings
              </Button>
            </Form>
          )}
        </Formik>
      </CardContent>
    </TabPanel>
  );
};

export default PrivacyTab;